import type { BooleanOperation } from "@/document/model/nodes";
import { ensureCanvasKit } from "@/renderer/text/canvasKitText";
import { boundsFromContours, contoursToPathData, pathDataToContours } from "./pathGeometry";
import { resolveBooleanGeometry, type BooleanGeometryResult, type BooleanOperandContours } from "./booleanGeometry";

type CanvasKitApi = NonNullable<Awaited<ReturnType<typeof ensureCanvasKit>>>;

let loadedCanvasKit: CanvasKitApi | undefined;

export async function resolveBooleanGeometryWithPathOps(operation: BooleanOperation, operands: BooleanOperandContours[]): Promise<BooleanGeometryResult> {
  const canvasKit = await ensureCanvasKit();
  if (!canvasKit) {
    return resolveBooleanGeometry(operation, operands);
  }
  loadedCanvasKit = canvasKit;

  return resolveWithCanvasKit(canvasKit, operation, operands);
}

export function resolveBooleanGeometryWithAvailablePathOps(operation: BooleanOperation, operands: BooleanOperandContours[]): BooleanGeometryResult {
  if (!loadedCanvasKit || operands.some(operand => !operand.path?.trim())) {
    return resolveBooleanGeometry(operation, operands);
  }

  return resolveWithCanvasKit(loadedCanvasKit, operation, operands);
}

function resolveWithCanvasKit(canvasKit: CanvasKitApi, operation: BooleanOperation, operands: BooleanOperandContours[]): BooleanGeometryResult {
  const paths = operands.flatMap(operand => {
    const pathData = operand.path?.trim() || contoursToPathData(operand.contours);
    const path = pathData ? canvasKit.Path.MakeFromSVGString(pathData) : null;
    return path ? [path] : [];
  });

  if (paths.length === 0) {
    return emptyResult();
  }
  if (paths.length === 1) {
    const path = paths[0]!;
    try {
      return resultFromPath(path.toSVGString());
    } finally {
      path.delete();
    }
  }

  const [first, ...rest] = paths;
  let result = first!.copy();
  try {
    for (const path of rest) {
      const combined = canvasKit.Path.MakeFromOp(result, path, pathOp(canvasKit, operation));
      result.delete();
      if (!combined) {
        result = new canvasKit.Path();
        continue;
      }
      result = combined;
    }

    return resultFromPath(result.toSVGString(), result.computeTightBounds());
  } finally {
    result.delete();
    for (const path of paths) {
      path.delete();
    }
  }
}

function pathOp(canvasKit: CanvasKitApi, operation: BooleanOperation) {
  switch (operation) {
    case "union":
      return canvasKit.PathOp.Union;
    case "subtract":
      return canvasKit.PathOp.Difference;
    case "intersect":
      return canvasKit.PathOp.Intersect;
    case "exclude":
      return canvasKit.PathOp.XOR;
  }
}

function resultFromPath(path: string, canvasKitBounds?: Float32Array | number[]): BooleanGeometryResult {
  const contours = pathDataToContours(path, 12);
  return {
    path,
    contours,
    bounds: canvasKitBounds ? rectFromCanvasKitBounds(canvasKitBounds) : boundsFromContours(contours),
  };
}

function emptyResult(): BooleanGeometryResult {
  return { contours: [], path: "", bounds: { x: 0, y: 0, width: 0, height: 0 } };
}

function rectFromCanvasKitBounds(bounds: Float32Array | number[]): BooleanGeometryResult["bounds"] {
  const left = bounds[0] ?? 0;
  const top = bounds[1] ?? 0;
  const right = bounds[2] ?? left;
  const bottom = bounds[3] ?? top;
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}
